/**
 * How many photos one listing may carry — the single source of truth.
 *
 * Every surface reads this: the organic sell page, `createVendoo`, and the admin edit
 * endpoint. They previously disagreed (8 in three places, 12 in the sell copy, and a
 * hardcoded 8 in the uploader that contradicted that copy), which is exactly the drift
 * this constant exists to prevent.
 *
 * WHY IT WAS 8, AND WHY THAT WAS WRONG
 * `LISTINGS_METADATA.photo0..photo7` holds foreign-hosted photo URLs for the external
 * feeds listed in `METADATA_PHOTO_SOURCES` (27-31, 34) — and only those. Every other
 * listing, organic and Vendoo alike, is dataSourceId 1 and resolves photos through
 * `getListingPhotos()`, which addresses them by index in S3 and has no such limit.
 * The 8 leaked out of those columns into places they never governed; thousands of
 * listings already carry more than 8 photos and render correctly.
 *
 * WHY THERE IS STILL A LIMIT
 * `SUPPLY_LISTINGS.photoOrder` is `VARCHAR(100)` and stores the display order as a
 * JSON array of indices: 20 photos is 51 characters, 33 is 90. Raising this much past
 * 30 means widening that column first. `createVendoo` is also unauthenticated, and
 * every photo costs another signed-URL generation.
 */
export const MAX_LISTING_PHOTOS = 20;

/**
 * True when a photo URL points at storage we control, and therefore has (or can
 * have) the resizer's `_<size>x<size>.{webp,avif}` siblings.
 *
 * This is the client-safe twin of `isOwnBucketUrl` in utils/external-photo-mirror.ts,
 * which cannot be imported into a component: that module pulls in the AWS SDK.
 * Keep the two in step — a test pins them together.
 *
 * WHY THIS IS A URL TEST AND NOT A DATA-SOURCE TEST
 * The renderer used to decide with `usesMetadataPhotos(dataSourceId)` and hand back
 * the URL untouched for the feed sources. That went stale: those feeds are mirrored
 * into our own bucket now, so their photos DO have variants, and the source-id check
 * was denying them size negotiation and AVIF both — on 29,907 live listings in the
 * last 90 days alone. The data layer had already moved to a URL rule (see
 * `foreignPhotoUrls`); this brings the renderer in line with it.
 *
 * A source-id list has to be edited every time a feed migrates. A URL test is right
 * on its own, before and after, for each listing independently.
 */
export function isOurPhotoUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return (
    url.includes('antengo-listings.s3.') ||
    url.includes('antengo-listings-dev.s3.') ||
    url.includes('listings-static')
  );
}
